const cache = new Map<string, { data: unknown; expiry: number }>();
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
const STORAGE_PREFIX = 'dehleze_cache:';

function hasWindowStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function getCachedData<T>(key: string): T | null {
  const entry = cache.get(key);
  if (entry) {
    if (Date.now() > entry.expiry) {
      cache.delete(key);
      return null;
    }
    return entry.data as T;
  }

  if (hasWindowStorage()) {
    try {
      const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
      if (raw) {
        const stored = JSON.parse(raw) as { data: unknown; expiry: number };
        if (Date.now() > stored.expiry) {
          window.localStorage.removeItem(STORAGE_PREFIX + key);
          return null;
        }
        cache.set(key, { data: stored.data, expiry: stored.expiry });
        return stored.data as T;
      }
    } catch {
      // ignore JSON / storage errors
    }
  }

  return null;
}

export function setCacheData(key: string, data: unknown, ttl = DEFAULT_TTL): void {
  const expiry = Date.now() + ttl;
  const entry = { data, expiry };
  cache.set(key, entry);

  if (hasWindowStorage()) {
    try {
      window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
    } catch {
      // ignore quota / storage errors
    }
  }
}

const pendingRequests = new Map<string, Promise<unknown>>();

/** Returns cached data or runs fetcher once; deduplicates concurrent in-flight requests. */
export async function fetchWithCache<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttl = DEFAULT_TTL
): Promise<T> {
  const cached = getCachedData<T>(key);
  if (cached !== null) return cached;

  const pending = pendingRequests.get(key);
  if (pending) return pending as Promise<T>;

  const promise = fetcher()
    .then((data) => {
      setCacheData(key, data, ttl);
      return data;
    })
    .finally(() => {
      pendingRequests.delete(key);
    });

  pendingRequests.set(key, promise);
  return promise;
}

export function clearCache(pattern?: string): void {
  if (!pattern) {
    cache.clear();
    if (hasWindowStorage()) {
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < window.localStorage.length; i += 1) {
          const k = window.localStorage.key(i);
          if (k && k.startsWith(STORAGE_PREFIX)) keysToRemove.push(k);
        }
        keysToRemove.forEach((k) => window.localStorage.removeItem(k));
      } catch {
        // ignore storage errors
      }
    }
    return;
  }

  const re = new RegExp(pattern);
  for (const key of cache.keys()) {
    if (re.test(key)) cache.delete(key);
  }

  if (hasWindowStorage()) {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith(STORAGE_PREFIX)) {
          const logicalKey = k.substring(STORAGE_PREFIX.length);
          if (re.test(logicalKey)) keysToRemove.push(k);
        }
      }
      keysToRemove.forEach((k) => window.localStorage.removeItem(k));
    } catch {
      // ignore storage errors
    }
  }
}
